import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { GlassCard } from "@/components/common/GlassCard";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusPill } from "@/components/common/StatusPill";
import { LoadingState, EmptyState } from "@/components/common/States";
import { CountUp } from "@/components/common/CountUp";
import { Button } from "@/components/ui/button";
import {
  Activity, Award, CalendarClock, MessageSquare, Target, TrendingUp, TrendingDown, Minus,
  Users, Sparkles, Zap, Shield, Brain, FileText, FolderOpen, Mic, ArrowUpRight, Quote, Layers,
  CheckCircle2, Circle, Loader2, Hexagon,
} from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, Tooltip as RTooltip, Cell } from "recharts";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "@/lib/utils";
import { AIActivityWidget } from "@/components/common/AIActivityWidget";

function makeSpark(seed = 1) {
  const arr = [];
  let v = 50 + seed * 5;
  for (let i = 0; i < 14; i++) {
    v = Math.max(10, v + (Math.random() - 0.4) * 12 + seed);
    arr.push({ v: Math.round(v) });
  }
  return arr;
}

function MiniSpark({ data, color = "#22D3EE" }) {
  return (
    <div className="h-12 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data}>
          <defs>
            <linearGradient id={`spark-${color}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.45} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area type="monotone" dataKey="v" stroke={color} strokeWidth={2} fill={`url(#spark-${color})`} dot={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function StatCard({ label, value, delta, deltaTone = "emerald", spark, testId }) {
  return (
    <GlassCard hover className="p-5 col-span-12 md:col-span-3 lg:col-span-3" testId={testId}>
      <div className="flex items-start justify-between">
        <div className="text-3 text-xs uppercase tracking-wider">{label}</div>
        {delta != null && (
          <StatusPill tone={deltaTone}>
            <TrendingUp className="h-3 w-3" /> {delta}
          </StatusPill>
        )}
      </div>
      <div className="mt-3 text-3xl font-semibold text-1">
        <CountUp value={value} />
      </div>
      {spark && <div className="mt-3">{spark}</div>}
    </GlassCard>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    let cancelled = false;
    api
      .get("/dashboard/summary")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {
        if (!cancelled) setData({ role: user?.role || "admin", metrics: { total_players: 0, total_evaluations: 0, total_users: 0, total_messages: 0 }, recent_players: [] });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user?.role]);

  if (loading) return <LoadingState />;
  if (!data) return <EmptyState icon={Activity} title="No data yet" />;

  return (
    <div className="max-w-[1500px] mx-auto">
      <PageHeader
        testId="dashboard-header"
        title={`Welcome back, ${user?.full_name?.split(" ")[0]}`}
        subtitle={user?.role === "admin" ? "Front-office overview — your roster at a glance." : "Your draft readiness, top fits, and next steps."}
      />
      {user?.role === "admin" ? <AdminDashboard data={data} nav={nav} /> : <PlayerBentoDashboard nav={nav} />}
    </div>
  );
}

function AdminDashboard({ data, nav }) {
  const m = data.metrics || {};
  return (
    <div className="grid grid-cols-12 gap-4 md:gap-5 lg:gap-6">
      <StatCard testId="dashboard-kpi-players" label="Active Players" value={m.total_players || 0} delta="+2" spark={<MiniSpark data={makeSpark(1)} />} />
      <StatCard testId="dashboard-kpi-evaluations" label="Evaluations" value={m.total_evaluations || 0} delta="+1" spark={<MiniSpark data={makeSpark(2)} color="#34D399" />} />
      <StatCard testId="dashboard-kpi-users" label="Users" value={m.total_users || 0} delta="+3" spark={<MiniSpark data={makeSpark(3)} color="#60A5FA" />} />
      <StatCard testId="dashboard-kpi-messages" label="Messages" value={m.total_messages || 0} delta="new" deltaTone="cyan" spark={<MiniSpark data={makeSpark(4)} color="#FBBF24" />} />

      <AIActivityWidget />

      <GlassCard className="col-span-12 lg:col-span-5 p-6" testId="dashboard-quick-actions">
        <div className="flex items-center justify-between">
          <h3 className="text-1 font-semibold">Quick actions</h3>
          <span className="text-3 text-xs">Admin</span>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <ActionTile testId="qa-create-player" icon={Users} label="Create Player" onClick={() => nav("/app/admin")} />
          <ActionTile testId="qa-view-roster" icon={Award} label="View Roster" onClick={() => nav("/app/players")} />
          <ActionTile testId="qa-messaging" icon={MessageSquare} label="Open Messaging" onClick={() => nav("/app/messaging")} />
          <ActionTile testId="qa-team-fits" icon={Target} label="Team Fits" onClick={() => nav("/app/players")} />
          <ActionTile testId="qa-pitch-deck" icon={Sparkles} label="View Pitch Deck" onClick={() => nav("/pitch")} />
        </div>
        <div className="mt-6">
          <div className="text-3 text-xs uppercase tracking-wider">Activity</div>
          <div className="mt-2 space-y-2">
            {(data.recent_players || []).slice(0, 3).map((p) => (
              <div key={p.id} className="flex items-center gap-3 text-sm py-2">
                <div className="h-8 w-8 rounded-full bg-white/5 ring-1 ring-white/10 grid place-items-center text-xs">{p.full_name.slice(0, 1)}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-1 truncate">{p.full_name}</div>
                  <div className="text-3 text-xs">added · {p.position}</div>
                </div>
                <StatusPill tone="neutral">{p.school}</StatusPill>
              </div>
            ))}
          </div>
        </div>
      </GlassCard>

      <GlassCard className="col-span-12 p-6 lg:p-8 overflow-hidden relative" testId="dashboard-hero-card">
        <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full pointer-events-none" style={{ background: "radial-gradient(closest-side, rgba(34,211,238,0.22), transparent 70%)" }} />
        <div className="flex items-center gap-2 text-3 text-xs"><Activity className="h-3.5 w-3.5" /> LIVE OVERVIEW</div>
        <h2 className="mt-2 text-3xl lg:text-4xl font-semibold tracking-tight text-1">Roster Draft Pipeline</h2>
        <p className="text-2 text-sm mt-1 max-w-2xl">Quickly scan recent additions and jump into the player you're prepping next.</p>
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {(data.recent_players || []).slice(0, 4).map((p) => (
            <button key={p.id} onClick={() => nav(`/app/players/${p.id}/profile`)} data-testid={`dashboard-recent-player-${p.id}`} className="glass glass-hover text-left p-4">
              <div className="flex items-center justify-between">
                <div className="text-1 font-semibold">{p.full_name}</div>
                <StatusPill tone="cyan">{p.position}</StatusPill>
              </div>
              <div className="text-2 text-xs mt-1">{p.school} · {p.class_year}</div>
              <div className="mt-3 flex items-center gap-2 text-xs text-3">
                <Award className="h-3.5 w-3.5" /> {p.projected_round || "—"}
                <span className="text-3">·</span>
                <span>{p.projected_pick_range || ""}</span>
              </div>
            </button>
          ))}
        </div>
        <div className="mt-6 flex gap-2">
          <Button data-testid="dashboard-cta-roster" onClick={() => nav("/app/players")} className="cta-primary">View Roster</Button>
          <Button data-testid="dashboard-cta-admin" onClick={() => nav("/app/admin")} variant="ghost" className="text-2 hover:text-white">Admin Panel</Button>
        </div>
      </GlassCard>
    </div>
  );
}

function ActionTile({ icon: Icon, label, onClick, testId }) {
  return (
    <button data-testid={testId} onClick={onClick} className="glass glass-hover p-4 text-left">
      <Icon className="h-4 w-4 text-cyan-300" />
      <div className="mt-3 text-1 text-sm font-medium">{label}</div>
    </button>
  );
}

// =============================================================================
// PLAYER BENTO DASHBOARD - 10 cards
// =============================================================================
function PlayerBentoDashboard({ nav }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    api.get("/dashboard/cards").then((r) => { if (!cancelled) setD(r.data); }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);
  if (loading) return <LoadingState />;
  if (!d?.ready) return <EmptyState icon={Hexagon} title="Profile not ready" description="Your advisor hasn't finished setting up your profile yet." />;
  const player = d.player;

  return (
    <div className="grid grid-cols-12 gap-4 md:gap-5">
      <DraftProjectionHero data={d} nav={nav} />
      <PlayerGradeCard data={d.player_grade} oneLiner={d.one_liner} nav={() => nav("/app/me/evaluation")} />
      <WeeklyTrendCard data={d.weekly_trend} />
      <StrengthsCard items={d.strengths} />
      <WeaknessesCard items={d.weaknesses} />
      <WeeklyActionCard items={d.weekly_actions} weeklyPriorities={d.weekly_priorities} nav={() => nav("/app/me/draft-plan")} />
      <InterviewPrepCard data={d.interview} nav={() => nav("/app/me/interview-prep")} />
      <TeamFitsCard items={d.team_fits} nav={() => nav("/app/me/team-fits")} />
      <NarrativeCard player={player} text={d.narrative_md} />
      <DocumentsCard data={d.documents} nav={() => nav("/app/me/documents")} reportsNav={() => nav("/app/me/reports")} />
    </div>
  );
}

// 1. Draft Projection — Large Hero Card
function DraftProjectionHero({ data, nav }) {
  const proj = data.projection || {};
  const trend = proj.trend || "stable";
  const TrendIcon = trend === "up" ? TrendingUp : trend === "down" ? TrendingDown : Minus;
  const trendTone = trend === "up" ? "emerald" : trend === "down" ? "rose" : "neutral";
  const updated = proj.updated_at ? new Date(proj.updated_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";

  return (
    <GlassCard className="col-span-12 lg:col-span-8 p-6 lg:p-8 overflow-hidden relative" testId="dashboard-card-projection">
      <div className="absolute -top-24 -right-20 h-80 w-80 rounded-full pointer-events-none" style={{ background: "radial-gradient(closest-side, rgba(34,211,238,0.22), transparent 70%)" }} />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-3 text-xs"><Activity className="h-3.5 w-3.5" /> CURRENT PROJECTION</div>
        <StatusPill tone={trendTone}><TrendIcon className="h-3 w-3" /> {trend.toUpperCase()}</StatusPill>
      </div>
      <div className="mt-2 flex items-end gap-3 flex-wrap">
        <h2 className="text-4xl lg:text-5xl font-semibold tracking-tight text-1">{proj.round || "Unprojected"}</h2>
        {proj.pick_range && <div className="text-2 text-sm pb-2">{proj.pick_range}</div>}
      </div>
      <div className="mt-3 flex items-center gap-3 flex-wrap">
        <div className="text-3 text-[10px] uppercase tracking-wider">CONFIDENCE</div>
        <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden max-w-xs min-w-[120px]">
          <div className="h-full bg-gradient-to-r from-cyan-400 to-blue-500" style={{ width: `${proj.confidence || 0}%` }} />
        </div>
        <div className="text-1 text-sm font-mono">{proj.confidence || 0}%</div>
        <div className="text-3 text-[10px] uppercase tracking-wider">UPDATED</div>
        <div className="text-2 text-xs font-mono">{updated}</div>
      </div>
      {proj.explanation_md && (
        <div className="mt-5 glass p-4">
          <div className="flex items-center gap-2 text-3 text-[10px] uppercase tracking-wider mb-2"><Sparkles className="h-3 w-3" /> AI Read</div>
          <div className="markdown-body text-sm">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{proj.explanation_md}</ReactMarkdown>
          </div>
        </div>
      )}
      <div className="mt-5 flex gap-2">
        <Button data-testid="dashboard-view-evaluation" onClick={() => nav("/app/me/evaluation")} className="cta-primary">Open Evaluation <ArrowUpRight className="h-4 w-4" /></Button>
        <Button data-testid="dashboard-view-draft-plan" onClick={() => nav("/app/me/draft-plan")} variant="ghost" className="text-2 hover:text-white">Draft Plan</Button>
      </div>
    </GlassCard>
  );
}

// 2. Player Grade
function PlayerGradeCard({ data, oneLiner, nav }) {
  const groups = [
    { label: "General", value: data.general, icon: Layers, color: "#22D3EE" },
    { label: "Position", value: data.position, icon: Target, color: "#60A5FA" },
  ];
  const overall = data.overall ?? data.recommended;
  const tier = data.tier || {};
  return (
    <GlassCard className="col-span-12 md:col-span-6 lg:col-span-4 p-6" testId="dashboard-card-grade">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-3 text-xs uppercase tracking-wider">Player Grade</div>
          <h3 className="text-1 font-semibold mt-1">Overall</h3>
        </div>
        {data.overridden && <StatusPill tone="amber">Overridden</StatusPill>}
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <div className="text-5xl font-semibold text-1 font-mono">{overall != null ? overall.toFixed(1) : "—"}</div>
        <div className="text-3 text-sm">/ 9.0</div>
      </div>
      {tier.label && <StatusPill tone={tier.tone || "neutral"} className="mt-1.5">{tier.label}</StatusPill>}
      <div className="mt-4 space-y-2.5">
        {groups.map((g) => {
          const Icon = g.icon;
          const v = g.value;
          return (
            <div key={g.label} className="flex items-center gap-3">
              <Icon className="h-3.5 w-3.5" style={{ color: g.color }} />
              <span className="text-2 text-xs flex-1">{g.label}</span>
              <div className="flex-1 h-1 bg-white/5 rounded-full overflow-hidden max-w-[100px]">
                <div className="h-full rounded-full" style={{ width: `${((v || 0) / 9) * 100}%`, background: g.color }} />
              </div>
              <span className="text-1 text-xs font-mono w-9 text-right">{v != null ? v.toFixed(1) : "—"}</span>
            </div>
          );
        })}
      </div>
      {oneLiner && (
        <div className="mt-4 glass p-3">
          <div className="flex items-start gap-2">
            <Quote className="h-3 w-3 text-cyan-300 mt-0.5 shrink-0" />
            <p className="text-2 text-xs leading-relaxed italic">{oneLiner}</p>
          </div>
        </div>
      )}
      <button onClick={nav} className="mt-3 text-cyan-300 text-xs flex items-center gap-1 hover:text-cyan-200">View detail <ArrowUpRight className="h-3 w-3" /></button>
    </GlassCard>
  );
}

// 3. Weekly Trend
function WeeklyTrendCard({ data }) {
  const dir = data?.direction || "stable";
  const Icon = dir === "up" ? TrendingUp : dir === "down" ? TrendingDown : Minus;
  const tone = dir === "up" ? "emerald" : dir === "down" ? "rose" : "neutral";
  const sparkData = makeSpark(dir === "up" ? 3 : dir === "down" ? -2 : 0);
  const color = dir === "up" ? "#34D399" : dir === "down" ? "#FB7185" : "#94A3B8";
  return (
    <GlassCard className="col-span-12 sm:col-span-6 lg:col-span-4 p-6" testId="dashboard-card-trend">
      <div className="flex items-center justify-between">
        <div className="text-3 text-xs uppercase tracking-wider">Weekly Trend</div>
        <StatusPill tone={tone}><Icon className="h-3 w-3" /> {dir.toUpperCase()}</StatusPill>
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <div className="text-3xl font-semibold text-1 font-mono">{(data?.delta ?? 0) > 0 ? "+" : ""}{(data?.delta ?? 0).toFixed(1)}</div>
        <div className="text-3 text-xs">grade Δ</div>
      </div>
      <p className="text-2 text-xs mt-2 leading-relaxed">{data?.note}</p>
      <div className="mt-3"><MiniSpark data={sparkData} color={color} /></div>
    </GlassCard>
  );
}

// 4. Strengths
function StrengthsCard({ items }) {
  return (
    <GlassCard className="col-span-12 sm:col-span-6 lg:col-span-4 p-6" testId="dashboard-card-strengths">
      <div className="flex items-center justify-between">
        <div className="text-3 text-xs uppercase tracking-wider">Strengths</div>
        <StatusPill tone="emerald">Top {Math.min(items?.length || 0, 5)}</StatusPill>
      </div>
      <ul className="mt-4 space-y-2.5">
        {(items || []).slice(0, 5).map((s, i) => (
          <li key={i} className="flex items-start gap-2.5">
            <div className="mt-1 h-1.5 w-1.5 rounded-full bg-emerald-300 shrink-0" />
            <span className="text-2 text-sm leading-relaxed">{s}</span>
          </li>
        ))}
        {(items || []).length === 0 && <li className="text-2 text-sm">Not assessed yet — generate AI report to populate.</li>}
      </ul>
    </GlassCard>
  );
}

// 5. Weaknesses / Areas to Improve
function WeaknessesCard({ items }) {
  return (
    <GlassCard className="col-span-12 sm:col-span-6 lg:col-span-4 p-6" testId="dashboard-card-weaknesses">
      <div className="flex items-center justify-between">
        <div className="text-3 text-xs uppercase tracking-wider">Areas to Improve</div>
        <StatusPill tone="amber">Priority</StatusPill>
      </div>
      <ul className="mt-4 space-y-2.5">
        {(items || []).slice(0, 5).map((s, i) => (
          <li key={i} className="flex items-start gap-2.5">
            <div className={cn("mt-0.5 h-5 w-5 rounded-md grid place-items-center text-[10px] font-mono font-semibold ring-1 shrink-0", i === 0 ? "bg-rose-400/15 ring-rose-300/40 text-rose-300" : i === 1 ? "bg-amber-400/15 ring-amber-300/40 text-amber-300" : "bg-white/5 ring-white/10 text-2")}>{i + 1}</div>
            <span className="text-2 text-sm leading-relaxed">{s}</span>
          </li>
        ))}
        {(items || []).length === 0 && <li className="text-2 text-sm">Not assessed yet — generate AI report to populate.</li>}
      </ul>
    </GlassCard>
  );
}

// 6. Weekly Action Plan
function WeeklyActionCard({ items, weeklyPriorities, nav }) {
  const wp = weeklyPriorities;
  return (
    <GlassCard className="col-span-12 lg:col-span-6 p-6" testId="dashboard-card-actions">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-cyan-300" />
          <h3 className="text-1 font-semibold">Weekly Action Plan</h3>
        </div>
        <button onClick={nav} className="text-cyan-300 text-xs hover:text-cyan-200 flex items-center gap-1">Open plan <ArrowUpRight className="h-3 w-3" /></button>
      </div>

      {wp ? (
        <div className="mt-3" data-testid="dashboard-weekly-priorities">
          {wp.week_focus && (
            <div className="glass p-3 mb-3">
              <div className="text-3 text-[10px] uppercase tracking-wider">This Week's Focus</div>
              <div className="text-1 text-sm mt-1">{wp.week_focus}</div>
            </div>
          )}
          {(wp.priorities || []).length > 0 && (
            <ol className="space-y-2">
              {wp.priorities.slice(0, 3).map((p, idx) => {
                const tone = p.status === "complete" ? "emerald" : p.status === "in_progress" ? "cyan" : "neutral";
                const Icon = p.status === "complete" ? CheckCircle2 : p.status === "in_progress" ? Loader2 : Circle;
                return (
                  <li key={p.id} className="flex items-center gap-3 glass p-3" data-testid={`dashboard-weekly-priority-${idx}`}>
                    <div className="h-6 w-6 rounded-full bg-cyan-300/15 ring-1 ring-cyan-300/30 grid place-items-center text-cyan-200 text-[11px] font-mono shrink-0">{idx + 1}</div>
                    <Icon className={cn("h-4 w-4 shrink-0", p.status === "complete" ? "text-emerald-300" : p.status === "in_progress" ? "text-cyan-300 animate-spin-slow" : "text-white/40")} />
                    <div className="flex-1 min-w-0">
                      <div className={cn("text-1 text-sm font-medium truncate", p.status === "complete" && "line-through text-3")}>{p.title}</div>
                      {p.owner_name && <div className="text-3 text-xs mt-0.5">{p.owner_name}</div>}
                    </div>
                    <StatusPill tone={tone}>{(p.status || "not_started").replace("_", " ")}</StatusPill>
                  </li>
                );
              })}
            </ol>
          )}
          <div className="flex items-center justify-between mt-3 text-3 text-[11px]">
            <span>Tasks: <span className="text-1 font-mono">{wp.tasks_done ?? 0}/{wp.tasks_total ?? 0} done</span></span>
            {wp.next_meeting_date && <span>Next meeting <span className="text-1 font-mono">{wp.next_meeting_date}</span></span>}
          </div>
        </div>
      ) : (
        <div className="mt-4 space-y-2">
          {(items || []).map((m) => {
            const Icon = m.status === "complete" ? CheckCircle2 : m.status === "in_progress" ? Loader2 : Circle;
            const tone = m.status === "complete" ? "emerald" : m.status === "in_progress" ? "cyan" : "neutral";
            return (
              <div key={m.id} className="flex items-center gap-3 glass p-3">
                <Icon className={cn("h-4 w-4 shrink-0", m.status === "complete" ? "text-emerald-300" : m.status === "in_progress" ? "text-cyan-300 animate-spin-slow" : "text-white/40")} />
                <div className="flex-1 min-w-0">
                  <div className="text-1 text-sm font-medium truncate">{m.title}</div>
                  {m.description && <div className="text-3 text-xs truncate mt-0.5">{m.description}</div>}
                </div>
                <div className="text-3 text-[11px] font-mono">{m.due_date || ""}</div>
                <StatusPill tone={tone}>{(m.status || "planned").replace("_", " ")}</StatusPill>
              </div>
            );
          })}
          {(items || []).length === 0 && <div className="text-2 text-sm">Nothing due this week. Solid.</div>}
        </div>
      )}
    </GlassCard>
  );
}

// 7. Interview Prep
function InterviewPrepCard({ data, nav }) {
  return (
    <GlassCard className="col-span-12 sm:col-span-6 lg:col-span-3 p-6" testId="dashboard-card-interview">
      <div className="flex items-center justify-between">
        <div className="text-3 text-xs uppercase tracking-wider">Interview Prep</div>
        <Mic className="h-4 w-4 text-cyan-300" />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <div className="glass p-3">
          <div className="text-3 text-[10px] uppercase tracking-wider">Last Score</div>
          <div className="text-1 text-2xl font-semibold font-mono mt-0.5">{data?.avg_rating ? `${data.avg_rating}/5` : "—"}</div>
        </div>
        <div className="glass p-3">
          <div className="text-3 text-[10px] uppercase tracking-wider">Confidence</div>
          <div className="text-1 text-2xl font-semibold font-mono mt-0.5">{data?.confidence_pct || 0}%</div>
        </div>
      </div>
      <div className="text-3 text-xs mt-3">{data?.answered || 0} of {data?.total_questions || 0} questions answered</div>
      <Button data-testid="dashboard-start-interview" onClick={nav} className="cta-primary w-full mt-4">Start Interview</Button>
    </GlassCard>
  );
}

// 8. Team Fits
function TeamFitsCard({ items, nav }) {
  return (
    <GlassCard className="col-span-12 sm:col-span-6 lg:col-span-3 p-6" testId="dashboard-card-team-fits">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Target className="h-4 w-4 text-cyan-300" />
          <h3 className="text-1 font-semibold">Team Fits</h3>
        </div>
        <button onClick={nav} className="text-cyan-300 text-xs hover:text-cyan-200">All</button>
      </div>
      <div className="mt-3 space-y-2">
        {(items || []).slice(0, 3).map((f, i) => (
          <div key={f.id} className="flex items-center gap-2.5">
            <div className="text-3 font-mono text-xs w-4">#{i + 1}</div>
            <div className="h-8 w-8 rounded-lg bg-white/5 ring-1 ring-white/10 grid place-items-center text-1 text-[11px] font-semibold">{f.team_abbreviation}</div>
            <div className="flex-1 min-w-0">
              <div className="text-1 text-xs font-medium truncate">{f.team_name}</div>
              <div className="mt-1 h-1 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-cyan-400 to-blue-500" style={{ width: `${f.fit_score}%` }} />
              </div>
            </div>
            <div className="text-1 text-sm font-mono">{f.fit_score}</div>
          </div>
        ))}
        {(items || []).length === 0 && <div className="text-2 text-sm">No team fits yet.</div>}
      </div>
    </GlassCard>
  );
}

// 9. Player Narrative
function NarrativeCard({ player, text }) {
  return (
    <GlassCard className="col-span-12 lg:col-span-6 p-6" testId="dashboard-card-narrative">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Quote className="h-4 w-4 text-cyan-300" />
          <h3 className="text-1 font-semibold">Player Narrative</h3>
        </div>
        <StatusPill tone="neutral">{player?.archetype || player?.position}</StatusPill>
      </div>
      <div className="mt-4 markdown-body text-sm">
        {text ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown> : <p className="text-2">No narrative yet — your advisor will add a short story to frame your draft profile.</p>}
      </div>
    </GlassCard>
  );
}

// 10. Documents
function DocumentsCard({ data, nav, reportsNav }) {
  const recent = data?.recent || [];
  return (
    <GlassCard className="col-span-12 lg:col-span-6 p-6" testId="dashboard-card-documents">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FolderOpen className="h-4 w-4 text-cyan-300" />
          <h3 className="text-1 font-semibold">Documents & Reports</h3>
        </div>
        <div className="flex items-center gap-2">
          <StatusPill tone="cyan">{data?.total || 0} docs</StatusPill>
          <StatusPill tone="ice">{data?.reports_total || 0} reports</StatusPill>
        </div>
      </div>
      <div className="mt-4 space-y-2">
        {recent.slice(0, 4).map((d) => (
          <div key={d.id} className="flex items-center gap-3 glass p-3">
            <div className="h-8 w-8 rounded-lg bg-white/5 ring-1 ring-white/10 grid place-items-center"><FileText className="h-3.5 w-3.5 text-cyan-300" /></div>
            <div className="flex-1 min-w-0">
              <div className="text-1 text-sm font-medium truncate">{d.name}</div>
              <div className="text-3 text-[11px] truncate">{d.type} · {new Date(d.created_at).toLocaleDateString()}</div>
            </div>
            <StatusPill tone="neutral">{d.type}</StatusPill>
          </div>
        ))}
        {recent.length === 0 && <div className="text-2 text-sm">No documents yet.</div>}
      </div>
      <div className="mt-4 flex gap-2">
        <Button data-testid="dashboard-open-documents" onClick={nav} className="cta-primary flex-1">Open Documents</Button>
        <Button data-testid="dashboard-open-reports" onClick={reportsNav} variant="ghost" className="text-2 hover:text-white flex-1">Reports</Button>
      </div>
    </GlassCard>
  );
}
